import OrderAttentionAlert from './OrderAttentionAlert';
import { Link } from 'react-router-dom';
import { Nav, Navbar, NavDropdown } from 'react-bootstrap';
import AccountMenu from './AccountMenu';
import MobileNavMenu from './MobileNavMenu';

// Shared top navigation (homepage/user_experience.md #5 + #9 + #10). Identical in every vertical;
// the per-vertical differences live in ../navConfig.js. Desktop (>= lg) renders the classic
// horizontal bar; on narrow screens the bar shows the portal name + the "RentalVerse" home link
// + the standalone bell + a hamburger menu button (all IN the bar - nothing floats), and the
// full menu opens as an off-canvas from <MobileNavMenu> (#4).
//
//   nav              the object from buildNav() in ../navConfig.js
//   isAuthenticated  from useAuth()
//   bell             optional node rendered as a standalone control in the header bar, kept
//                    OUTSIDE the collapsing menu so it stays visible on mobile (#9). car_rental only.
const HOMEPAGE_URL = process.env.REACT_APP_HOMEPAGE_URL || 'http://localhost:5175';
const SHOW_GLOBAL_NAV = process.env.REACT_APP_STANDALONE_DEPLOYMENT !== 'true';

function DesktopLinks({ nav, isAuthenticated }) {
  return (
    <Nav className="align-items-lg-center ms-lg-auto">
      {nav.browseCategories?.length > 0 && (
        <NavDropdown title="Browse" id="primary-browse-menu">
          <NavDropdown.Item as={Link} to="/">All listings</NavDropdown.Item>
          <NavDropdown.Divider />
          {nav.browseCategories.map((c) => (
            <NavDropdown.Item key={c.value} as={Link} to={`/?category=${encodeURIComponent(c.value)}`}>
              {c.label}
            </NavDropdown.Item>
          ))}
        </NavDropdown>
      )}

      {(nav.links || []).map((item, i) => {
        if (item.type === 'group') {
          return (
            <NavDropdown key={`g${i}`} title={item.label} id={`primary-nav-group-${i}`}>
              {item.items.map((s) => (
                <NavDropdown.Item key={s.to} as={Link} to={s.to}>{s.label}</NavDropdown.Item>
              ))}
            </NavDropdown>
          );
        }
        if (item.external) {
          return <Nav.Link key={`l${i}`} href={item.to}>{item.label}</Nav.Link>;
        }
        return (
          <Nav.Link key={`l${i}`} as={Link} to={item.to}>
            {item.live && <span aria-hidden="true">👁 </span>}
            {item.label}
          </Nav.Link>
        );
      })}

      {!isAuthenticated && (nav.loggedOutLinks || []).map((l) => (
        <Nav.Link key={l.to} as={Link} to={l.to}>{l.label}</Nav.Link>
      ))}
      {!isAuthenticated && <Nav.Link as={Link} to="/pricing">Pricing</Nav.Link>}

      {isAuthenticated ? (
        <AccountMenu />
      ) : (
        <>
          <Nav.Link as={Link} to="/login">Log in</Nav.Link>
          <Nav.Link as={Link} to="/signup">Sign up</Nav.Link>
        </>
      )}
    </Nav>
  );
}

export default function PrimaryNav({ nav, isAuthenticated, bell = null }) {
  return (
    <Navbar bg="dark" variant="dark" expand="lg" className="mb-4">
      <div className="container gap-2">
        {/* Portal name, then the RentalVerse home link (home icon + "RentalVerse") right next to
            it - always at the top of the bar, not buried in the menu. */}
        <Navbar.Brand as={Link} to="/" className="me-1">{nav.brand}</Navbar.Brand>
        {SHOW_GLOBAL_NAV && <a href={HOMEPAGE_URL} className="rv-home-link" aria-label="RentalVerse home">
          <span aria-hidden="true">&#127968;</span>&nbsp;RentalVerse
        </a>}

        {/* Desktop links. No <Navbar.Toggle> - below lg this stays collapsed and the in-bar
            hamburger in the right-hand cluster opens <MobileNavMenu> instead (#4). */}
        <Navbar.Collapse id="primary-nav" className="justify-content-end">
          <DesktopLinks nav={nav} isAuthenticated={isAuthenticated} />
        </Navbar.Collapse>

        {/* Right-hand cluster, always in the bar: the standalone bell (#9) + the mobile menu
            button. order-lg-last keeps it far-right on desktop; the menu button is d-lg-none. */}
        <div className="d-flex align-items-center gap-2 ms-auto order-lg-last">
          <OrderAttentionAlert navOnly />
          {bell && <div className="rv-notif-bell d-flex align-items-center">{bell}</div>}
          <MobileNavMenu nav={nav} isAuthenticated={isAuthenticated} />
        </div>
      </div>
    </Navbar>
  );
}
